import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FilePreview } from "@/components/FilePreview";
import { Check, X, Paperclip, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_TABS = [
  { key: "PENDING", label: "Menunggu" },
  { key: "APPROVED", label: "Disetujui" },
  { key: "REJECTED", label: "Ditolak" },
];

/**
 * Section review Berita Acara (BA DPS/DPSHP/DPT) untuk Admin+Panitia.
 * Diextract dari VerifikasiPage lama agar bisa dimount di LockProgressPage.
 */
export default function BaReviewSection() {
  const [tab, setTab] = useState("PENDING");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState(null);
  const [catatan, setCatatan] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ba-submissions", { params: { status: tab } });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const act = async () => {
    if (!review) return;
    try {
      const url = review.action === "approve"
        ? `/ba-submissions/${review.item.id}/approve`
        : `/ba-submissions/${review.item.id}/reject`;
      await api.post(url, { catatan: catatan.trim() });
      toast.success(review.action === "approve" ? "BA disetujui." : "BA ditolak.");
      setReview(null); setCatatan(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="space-y-4" data-testid="ba-review-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Panitia</div>
          <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Review Berita Acara
          </h2>
          <p className="text-slate-500 text-xs mt-1">Setujui BA DPS/DPSHP/DPT dari PPDP untuk naikkan phase TPS.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="ba-review-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {STATUS_TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`ba-tab-${t.key}`}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
                active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.label} {tab === t.key && `(${items.length})`}
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        {loading && <div className="text-sm text-slate-500 py-4 text-center">Memuat...</div>}
        {!loading && items.length === 0 && (
          <Card><CardContent className="pt-6 pb-6 text-center text-sm text-slate-500">
            Tidak ada pengajuan BA {STATUS_TABS.find((s) => s.key === tab)?.label.toLowerCase()}.
          </CardContent></Card>
        )}
        {items.map((ba) => (
          <BaCard
            key={ba.id}
            ba={ba}
            onApprove={() => setReview({ item: ba, action: "approve" })}
            onReject={() => setReview({ item: ba, action: "reject" })}
          />
        ))}
      </div>

      <Dialog open={!!review} onOpenChange={(v) => { if (!v) { setReview(null); setCatatan(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {review?.action === "approve" ? "Setujui BA" : "Tolak BA"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {review?.action === "approve"
                ? "BA akan disetujui dan PPDP dapat naik phase."
                : "Beri catatan agar PPDP dapat memperbaiki."}
            </p>
            <Input
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              placeholder="Catatan (opsional jika menyetujui)"
              className="h-11"
              data-testid="ba-review-catatan"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReview(null); setCatatan(""); }}>Batal</Button>
            <Button
              onClick={act}
              data-testid="ba-review-confirm"
              className={review?.action === "approve" ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-red-600 hover:bg-red-700 text-white"}
            >
              {review?.action === "approve" ? <Check className="h-4 w-4 mr-1" /> : <X className="h-4 w-4 mr-1" />}
              Konfirmasi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function BaCard({ ba, onApprove, onReject }) {
  const isPending = ba.status === "PENDING";
  const sigCls = ba.status === "APPROVED"
    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : ba.status === "REJECTED"
    ? "bg-red-100 text-red-800 border-red-200"
    : "bg-amber-100 text-amber-800 border-amber-200";
  return (
    <article className="rounded-md border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-base">BA {ba.doc_type}</span>
            <span className="text-xs font-semibold bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
              {ba.area_label}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Oleh <span className="font-semibold text-slate-700">{ba.submitted_by_name || ba.submitted_by_username}</span>
            · {new Date(ba.submitted_at).toLocaleString("id-ID")}
            · {ba.voter_count} pemilih
          </div>
        </div>
        {isPending ? (
          <div className="flex gap-2">
            <Button size="sm" onClick={onApprove} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`approve-ba-${ba.id}`}>
              <Check className="h-4 w-4 mr-1" /> Setujui
            </Button>
            <Button size="sm" variant="outline" onClick={onReject} className="text-red-700 border-red-200" data-testid={`reject-ba-${ba.id}`}>
              <X className="h-4 w-4 mr-1" /> Tolak
            </Button>
          </div>
        ) : (
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${sigCls}`}>
            {ba.status === "APPROVED" ? "Disetujui" : "Ditolak"}
          </span>
        )}
      </div>
      {ba.catatan && <div className="mt-2 text-xs"><span className="text-slate-500">Catatan PPDP:</span> {ba.catatan}</div>}
      {ba.review_catatan && <div className="mt-1 text-xs italic text-slate-600">Catatan reviewer: {ba.review_catatan}</div>}
      {ba.signed_file_ids?.filter(Boolean).length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">
            <Paperclip className="h-3 w-3" /> Foto BA ({ba.signed_file_ids.length})
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {ba.signed_file_ids.filter(Boolean).map((fid) => <FilePreview key={fid} fileId={fid} compact />)}
          </div>
        </div>
      )}
    </article>
  );
}
